import React, { useEffect, useState, useCallback } from "react";
import { Eye, EyeOff, Share2, MessageSquare, Check, X, Loader2, PenLine, Globe, Trash2, BarChart3, Award, Link2 } from "lucide-react";
import { toast } from "sonner";
import { Panel, Skeleton, Field, Segmented, EmptyState } from "../components/ui";
import Disclaimer from "../components/Disclaimer";
import CompletionChecklist from "../components/CompletionChecklist";
import RichEditor from "../components/RichEditor";
import PostEditor from "../components/PostEditor";
import TierBadge from "../components/TierBadge";
import { ProgressBar } from "../components/ui-extras";
import { toast as _t } from "sonner";
import useHashTarget from "../lib/useHashTarget";
import { api } from "../lib/api";
import { money, shortDate, num } from "../lib/format";
import { useTranslation } from "react-i18next";

// Where an investor controls what the world sees. Nothing here is public until they
// both publish the profile and share individual positions.
export default function InvestorStudio() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("profile");
  const [profile, setProfile] = useState(null);
  const [shared, setShared] = useState([]);
  const [txs, setTxs] = useState([]);
  const [queue, setQueue] = useState([]);
  useHashTarget(queue.length > 0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [commentaryFor, setCommentaryFor] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [rank, setRank] = useState(null);
  const [stats, setStats] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [note, setNote] = useState({ title: "", body: "", sentiment: "neutral" });

  const load = useCallback(() => {
    api.rankExplained().then((r) => setRank(r.data)).catch(() => {});
    api.postStats().then((r) => setStats(r.data)).catch(() => {});
    api.profile().then((r) => setCompletion(r.data.completion)).catch(() => {});
    // allSettled, not all. With Promise.all a single failing request rejects the whole
    // batch, the shared .catch swallows it, and *nothing* gets set — so a 500 in the
    // profile call silently emptied the positions list and the posts tab as well. The
    // investor sees a studio with no positions to share and a post they just wrote
    // missing, and nothing on screen says a request failed. Each section now stands or
    // falls on its own request.
    Promise.allSettled([api.myInvestorProfile(), api.transactions({}),
                        api.moderationQueue(), api.myPosts()])
      .then(([p, tx, q, po]) => {
        if (p.status === "fulfilled") {
          setProfile(p.value.data.profile);
          setShared(p.value.data.shared_transaction_ids || []);
        }
        if (tx.status === "fulfilled") setTxs(tx.value.data);
        if (q.status === "fulfilled") setQueue(q.value.data);
        if (po.status === "fulfilled") setPosts(po.value.data);
        const failed = [p, tx, q, po].filter((r) => r.status === "rejected");
        if (failed.length) toast.error(t("studio.partialLoad"));
      })
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.updateInvestorProfile({ ...profile, ...patch });
      setProfile(data.profile);
      toast.success(t("dialogs.saved"));
    } catch (e) {
      // 428 Precondition Required: the server is telling us the agreement is unsigned.
      if (e.response?.status === 428) {
        const { data } = await api.currentContract();
        setAgreement(data);
      } else {
        toast.error(e.response?.data?.detail || t("dialogs.failed"));
      }
    }
    finally { setSaving(false); }
  };

  const acceptAgreement = async () => {
    try {
      await api.acceptContract(agreement.version);
      setAgreement(null);
      toast.success(t("studio.agreementAccepted"));
      save({ published: true });
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const toggleShare = async (tx) => {
    const on = shared.includes(tx.id);
    try {
      if (on) { await api.unsharePosition(tx.id); setShared((s) => s.filter((x) => x !== tx.id)); }
      else { await api.sharePosition(tx.id); setShared((s) => [...s, tx.id]); }
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const saveNote = async () => {
    try {
      await api.saveCommentary(commentaryFor.id, note);
      toast.success(t("dialogs.saved"));
      setCommentaryFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  /** Open an existing post for editing.
   *
   * The list from `/posts/mine/all` carries only what the list needs — title, views,
   * visibility — so opening the editor straight off a row put the author in front of
   * an empty body and no stock tags, as if the post had been wiped. The full post is
   * fetched first; the author always passes `_can_read_full` on their own work. */
  const openPost = async (p) => {
    try {
      const { data } = await api.post(p.slug);
      setEditingPost({ ...data, id: p.id, published: p.published });
    } catch { toast.error(t("dialogs.failed")); }
  };

  const savePost = async () => {
    const p = editingPost;
    // A post made only of a chart is a legitimate post, so the requirement is a title
    // and *something* — not a title and prose.
    const hasContent = (p.blocks || []).some(
      (b) => (b.type === "text" && b.html?.replace(/<[^>]+>|&nbsp;|\s/g, "")) || b.type !== "text");
    if (!p.title?.trim() || !hasContent) { toast.error(t("posts.titleBodyRequired")); return; }
    // Charts are re-frozen on every save. An author editing a post gets a fresh
    // snapshot; a reader never gets a code path that writes here at all.
    try {
      if (p.id) await api.updatePost(p.id, p);
      else await api.createPost(p);
      setEditingPost(null);
      toast.success(t("dialogs.saved"));
      api.myPosts().then((r) => setPosts(r.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  const removePost = async (p) => {
    if (!window.confirm(t("posts.deleteConfirm"))) return;
    try { await api.deletePost(p.id); setPosts((ps) => ps.filter((x) => x.id !== p.id)); }
    catch { toast.error(t("dialogs.failed")); }
  };

  const moderate = async (c, action) => {
    try {
      await api.moderateComment(c.id, { action, reply: c._reply || "" });
      setQueue((qq) => qq.filter((x) => x.id !== c.id));
      toast.success(action === "approve" ? t("studio.approved") : t("studio.rejected"));
    } catch (e) { toast.error(e.response?.data?.detail || t("dialogs.failed")); }
  };

  if (loading) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-32" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("studio.subtitle")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("studio.title")}</h1>
        </div>
        <button
          data-testid="publish-toggle"
          className={profile?.published ? "btn-outline" : "btn-primary"}
          onClick={() => save({ published: !profile?.published })}
          disabled={saving}
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : profile?.published ? <EyeOff size={16} /> : <Eye size={16} />}
          {profile?.published ? t("studio.unpublish") : t("studio.publish")}
        </button>
      </div>

      {completion && <CompletionChecklist completion={completion} />}

      {!profile?.published && (
        <div className="rounded-xl bg-amber-50 border border-amber-100 p-3.5 text-sm text-amber-800" data-testid="studio-draft-notice">
          {t("studio.draftNotice")}
        </div>
      )}

      <Segmented
        options={[
          { value: "profile", label: t("studio.tabProfile") },
          { value: "positions", label: t("studio.tabPositions") },
          { value: "posts", label: t("studio.tabPosts") },
          { value: "rank", label: t("studio.tabRank") },
          { value: "moderation", label: `${t("studio.tabModeration")}${queue.length ? ` (${queue.length})` : ""}` },
        ]}
        value={tab} onChange={setTab} testid="studio-tabs" fullWidthOnMobile
      />

      {tab === "profile" && (
        <Panel className="card p-5 sm:p-6 space-y-4" testid="studio-profile">
          <Field label={t("studio.displayName")}>
            <input className="input" data-testid="studio-name" value={profile?.display_name || ""}
                   onChange={(e) => setProfile({ ...profile, display_name: e.target.value })} />
          </Field>
          <Field label={t("studio.headline")}>
            <input className="input" data-testid="studio-headline" value={profile?.headline || ""}
                   placeholder={t("studio.headlinePlaceholder")}
                   onChange={(e) => setProfile({ ...profile, headline: e.target.value })} />
          </Field>
          <Field label={t("studio.bio")}>
            <RichEditor testid="studio-bio" value={profile?.bio || ""} minHeight={150}
                        onChange={(html) => setProfile({ ...profile, bio: html })} />
          </Field>
          <Field label={t("studio.strategy")}>
            <RichEditor testid="studio-strategy" value={profile?.strategy || ""} minHeight={150}
                        placeholder={t("studio.strategyPlaceholder")}
                        onChange={(html) => setProfile({ ...profile, strategy: html })} />
          </Field>
          <button className="btn-primary w-full sm:w-auto justify-center" onClick={() => save({})} disabled={saving}
                  data-testid="studio-save">
            {saving && <Loader2 size={16} className="animate-spin" />} {t("common.save")}
          </button>
        </Panel>
      )}

      {tab === "positions" && (
        <Panel className="card overflow-hidden" testid="studio-positions">
          <div className="p-4 text-sm text-slate-500 border-b border-slate-100">{t("studio.positionsHelp")}</div>
          {txs.length === 0 ? (
            <EmptyState testid="studio-no-tx" title={t("transactions.emptyTitle")} description={t("transactions.emptyDesc")} />
          ) : (
            <div className="divide-y divide-slate-50">
              {txs.map((tx) => {
                const on = shared.includes(tx.id);
                return (
                  <div key={tx.id} className="p-4 flex items-center gap-3" data-testid={`studio-tx-${tx.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`pill ${tx.type === "buy" ? "bg-brand-50 text-brand-600" : "bg-amber-50 text-amber-700"}`}>
                          {tx.type.toUpperCase()}
                        </span>
                        <span className="font-medium text-slate-900">{tx.ticker}</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5 tabular">
                        {num(tx.quantity, 4)} @ {money(tx.price, tx.currency)} · {shortDate(tx.date)}
                      </div>
                    </div>
                    <button onClick={() => { setCommentaryFor(tx); setNote({ title: "", body: "", sentiment: "neutral" }); }}
                            className="p-2 rounded-lg text-slate-300 hover:text-brand-600 transition-colors"
                            data-testid={`note-${tx.id}`} title={t("studio.writeNote")}>
                      <MessageSquare size={16} />
                    </button>
                    <button onClick={() => toggleShare(tx)} data-testid={`share-${tx.id}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors ${
                              on ? "bg-brand-600 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                      <Share2 size={13} /> {on ? t("studio.shared") : t("studio.share")}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      )}

      {tab === "rank" && (
        <div className="space-y-5" data-testid="studio-rank">
          <Panel className="card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Award size={17} className="text-brand-500" />
                <h3 className="font-heading font-semibold tracking-tight text-lg">{t("rank.title")}</h3>
              </div>
              {rank && <TierBadge tier={rank.tier} />}
            </div>
            {!rank ? <Skeleton className="h-32 mt-4" /> : (
              <>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <div className="label">{t("rank.points")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1">{rank.points}</div>
                  </div>
                  <div>
                    <div className="label">{t("rank.return1y")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1">
                      {rank.return_pct == null ? "—" : `${rank.return_pct}%`}
                    </div>
                  </div>
                  <div>
                    <div className="label">{t("rank.return3y")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1">
                      {rank.return_3y_pct == null ? "—" : `${rank.return_3y_pct}%`}
                    </div>
                  </div>
                  <div>
                    <div className="label">{t("rank.months")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1">{rank.months_active ?? 0}</div>
                  </div>
                </div>

                {rank.tier_pending && (
                  <div className="mt-4 rounded-xl bg-amber-50 border border-amber-100 p-3.5 text-sm text-amber-900">
                    {t("rank.pending", { tier: t(`tiers.${rank.eligible_tier}`) })}
                  </div>
                )}

                {/* Two budgets, shown as two budgets. Craft is what you can earn
                    alone and it has a ceiling; audience is what other people give you
                    and it doesn't. Merging them into one list is what made the old
                    screen imply that posting harder was a route to Gold. */}
                {[
                  { bucket: "craft", total: rank.craft_points, cap: rank.craft_cap },
                  { bucket: "audience", total: rank.audience_points, cap: null },
                ].map(({ bucket, total, cap }) => (
                  <div className="mt-5" key={bucket}>
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <div className="label">{t(`rank.bucket.${bucket}`)}</div>
                      <div className="text-xs text-slate-400 tabular">
                        {cap ? t("rank.ofCap", { points: total ?? 0, cap }) : (total ?? 0)}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mb-1">{t(`rank.bucketHint.${bucket}`)}</p>
                    <div className="space-y-1">
                      {rank.lines.filter((l) => l.bucket === bucket).map((l) => (
                        <div key={l.key} className="flex items-center justify-between gap-4 py-2 border-b border-slate-50 last:border-0"
                             data-testid={`rank-line-${l.key}`}>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-800">{t(`guide.signal.${l.key}.name`, t(`rank.line.${l.key}`))}</div>
                            <div className="text-xs text-slate-400">
                              {l.each ? t("rank.each", { n: l.each }) : t("rank.awarded")}
                              {l.capped_at ? ` · ${t("rank.capped", { n: l.capped_at })}` : ""}
                            </div>
                          </div>
                          <span className="tabular font-semibold text-slate-900 shrink-0">{l.points}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {rank.progress?.next && (
                  <div className="mt-5 rounded-xl bg-slate-50 p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-slate-700">
                        {t("rank.toNext", { tier: t(`tiers.${rank.progress.next}`) })}
                      </span>
                      <span className="text-slate-400 tabular">{rank.progress.pct}%</span>
                    </div>
                    <ProgressBar pct={rank.progress.pct} className="mt-2" />
                    {/* Every gate, not only the points one. A bar sitting at 100%
                        while a hidden tenure or revenue requirement blocks the
                        promotion is the fastest way to make a fair ladder feel rigged. */}
                    <ul className="mt-3 space-y-1 text-xs" data-testid="rank-requirements">
                      {["points", "audience", "referrals", "months"].map((key) => {
                        const gap = rank.progress.requirements?.[key];
                        const met = gap == null;
                        return (
                          <li key={key} className={`flex items-start gap-1.5 ${met ? "text-slate-400" : "text-slate-600"}`}>
                            <span className={met ? "text-emerald-500" : "text-slate-300"}>{met ? "✓" : "•"}</span>
                            <span>
                              {t(`rank.req.${key}`, {
                                need: { points: rank.progress.points_needed, audience: rank.progress.min_audience,
                                        referrals: rank.progress.min_referrals, months: rank.progress.min_months }[key],
                                gap,
                              })}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {rank.progress.needs_approval && (
                      <p className="text-xs text-slate-500 mt-2">{t("rank.needsApproval")}</p>
                    )}
                  </div>
                )}
              </>
            )}
          </Panel>

          <Panel className="card p-5 sm:p-6" testid="studio-traffic">
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 size={17} className="text-brand-500" />
              <h3 className="font-heading font-semibold tracking-tight text-lg">{t("rank.trafficTitle")}</h3>
            </div>
            <p className="text-sm text-slate-500">{t("rank.trafficBody")}</p>
            {!stats ? <Skeleton className="h-32 mt-4" /> : (
              <>
                <div className="mt-4 grid grid-cols-3 gap-4">
                  <div>
                    <div className="label">{t("rank.totalViews")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1">{stats.total_views}</div>
                  </div>
                  <div>
                    <div className="label">{t("rank.publicViews")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1 text-brand-600">{stats.public_views}</div>
                  </div>
                  <div>
                    <div className="label">{t("rank.memberViews")}</div>
                    <div className="text-2xl font-heading font-semibold tabular mt-1">{stats.members_views}</div>
                  </div>
                </div>
                {stats.public_views === 0 && (
                  <div className="mt-4 rounded-xl bg-brand-50 border border-brand-100 p-3.5 text-sm text-brand-800">
                    {t("rank.writePublicNudge")}
                  </div>
                )}
                <div className="mt-4 divide-y divide-slate-50">
                  {stats.posts.map((po) => (
                    <div key={po.id} className="py-2.5 flex items-center gap-3" data-testid={`traffic-${po.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{po.title}</div>
                        <div className="text-xs text-slate-400">
                          {po.visibility === "public" ? t("posts.public") : t("posts.membersOnly")}
                          {po.comment_count ? ` · ${po.comment_count} ${t("investors.comments").toLowerCase()}` : ""}
                        </div>
                      </div>
                      <span className="tabular text-sm font-semibold shrink-0">{po.views}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Panel>
        </div>
      )}

      {tab === "posts" && (
        <Panel className="card overflow-hidden" testid="studio-posts">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
            <p className="text-sm text-slate-500">{t("posts.studioHelp")}</p>
            <button className="btn-primary shrink-0" data-testid="new-post"
                    onClick={() => setEditingPost({ title: "", excerpt: "", visibility: "subscribers",
                                                    published: true, tickers: [],
                                                    // One empty text block, so a new post opens on a
                                                    // place to write rather than on an "add" button.
                                                    blocks: [{ type: "text", html: "" }] })}>
              <PenLine size={15} /> {t("posts.new")}
            </button>
          </div>
          {posts.length === 0 ? (
            <p className="p-8 text-sm text-slate-400 text-center">{t("posts.none")}</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {posts.map((p) => (
                <div key={p.id} className="p-4 flex items-center gap-3" data-testid={`studio-post-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-slate-900 truncate">{p.title}</span>
                      {p.visibility === "public"
                        ? <span className="pill bg-emerald-50 text-emerald-600 flex items-center gap-1"><Globe size={10} /> {t("posts.public")}</span>
                        : <span className="pill bg-brand-50 text-brand-600">{t("posts.membersOnly")}</span>}
                      {!p.published && <span className="pill bg-slate-100 text-slate-500">{t("posts.draft")}</span>}
                    </div>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{p.excerpt}</p>
                  </div>
                  <button onClick={() => openPost(p)} data-testid={`edit-post-${p.id}`}
                          className="p-2 rounded-lg text-slate-300 hover:text-brand-600"><PenLine size={15} /></button>
                  <button onClick={() => removePost(p)} data-testid={`del-post-${p.id}`}
                          className="p-2 rounded-lg text-slate-300 hover:text-rose-500"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {editingPost && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setEditingPost(null)} />
          <div className="relative w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 space-y-4 max-h-[92vh] overflow-y-auto"
               data-testid="post-editor">
            <PostEditor post={editingPost} onChange={setEditingPost}
                        onSave={savePost} onCancel={() => setEditingPost(null)} />
          </div>
        </div>
      )}

      {tab === "moderation" && (
        <Panel className="card overflow-hidden" testid="studio-moderation">
          {queue.length === 0 ? (
            <p className="p-8 text-sm text-slate-400 text-center">{t("studio.noPending")}</p>
          ) : (
            <div className="divide-y divide-slate-50">
              {queue.map((c) => (
                <div key={c.id} id={`comment-${c.id}`} className="p-4 space-y-3" data-testid={`mod-${c.id}`}>
                  <div>
                    <span className="font-medium text-sm text-slate-800">{c.author_name}</span>
                    <p className="text-sm text-slate-600 mt-0.5">{c.body}</p>
                  </div>
                  <input className="input py-2 text-sm" placeholder={t("studio.replyPlaceholder")}
                         data-testid={`mod-reply-${c.id}`}
                         onChange={(e) => { c._reply = e.target.value; }} />
                  <div className="flex gap-2">
                    <button className="btn-primary py-1.5 text-sm" onClick={() => moderate(c, "approve")}
                            data-testid={`mod-approve-${c.id}`}>
                      <Check size={14} /> {t("studio.approve")}
                    </button>
                    <button className="btn-outline py-1.5 text-sm" onClick={() => moderate(c, "reject")}
                            data-testid={`mod-reject-${c.id}`}>
                      <X size={14} /> {t("studio.reject")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Panel>
      )}

      {commentaryFor && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setCommentaryFor(null)} />
          <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-2xl p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <h3 className="font-heading font-semibold text-lg">
              {t("studio.noteFor", { ticker: commentaryFor.ticker })}
            </h3>
            <Field label={t("studio.noteTitle")}>
              <input className="input" data-testid="note-title" value={note.title}
                     onChange={(e) => setNote({ ...note, title: e.target.value })} />
            </Field>
            <Field label={t("studio.noteBody")}>
              <RichEditor testid="note-body" value={note.body} minHeight={160}
                          placeholder={t("studio.notePlaceholder")}
                          onChange={(html) => setNote({ ...note, body: html })} />
            </Field>
            <Field label={t("studio.sentiment")}>
              <Segmented
                options={[
                  { value: "bullish", label: t("investors.sentiment.bullish") },
                  { value: "neutral", label: t("investors.sentiment.neutral") },
                  { value: "bearish", label: t("investors.sentiment.bearish") },
                ]}
                value={note.sentiment} onChange={(v) => setNote({ ...note, sentiment: v })}
                testid="note-sentiment" fullWidthOnMobile
              />
            </Field>
            <div className="flex flex-wrap justify-end gap-2">
              <button className="btn-ghost" onClick={() => setCommentaryFor(null)}>{t("common.cancel")}</button>
              <button className="btn-primary" onClick={saveNote} data-testid="note-save">{t("common.save")}</button>
            </div>
          </div>
        </div>
      )}

      {agreement && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-slate-900/50" />
          <div className="relative w-full sm:max-w-2xl bg-white rounded-t-3xl sm:rounded-2xl p-5 sm:p-6 max-h-[92vh] flex flex-col"
               data-testid="agreement-modal">
            <h3 className="font-heading font-semibold text-xl">{agreement.title || t("studio.agreementTitle")}</h3>
            <p className="text-sm text-slate-500 mt-1">{t("studio.agreementIntro")}</p>
            <div className="mt-4 flex-1 overflow-y-auto rounded-xl bg-slate-50 p-4 text-sm text-slate-700 whitespace-pre-line leading-relaxed">
              {agreement.body}
            </div>
            <div className="flex flex-wrap justify-end gap-2 mt-4">
              <button className="btn-ghost" onClick={() => setAgreement(null)}>{t("common.cancel")}</button>
              <button className="btn-primary" onClick={acceptAgreement} data-testid="accept-agreement">
                {t("studio.agreementAccept")}
              </button>
            </div>
          </div>
        </div>
      )}

      <Disclaimer />
    </div>
  );
}
